// API Configuration
import { getSelectedFarm } from '../utils/persistentStorage';

const getApiBaseUrl = (): string => {
  // In development, use the hardcoded localhost
  if (import.meta.env.DEV) {
    return 'http://localhost:4000';
  }
  
  // In production, use environment variable - REQUIRED for deployment
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    console.error('VITE_API_URL environment variable is not set in production!');
    throw new Error('API URL configuration is missing. Please set VITE_API_URL environment variable.');
  }
  return apiUrl;
};

export const API_BASE_URL = getApiBaseUrl();

// API endpoints
export const API_ENDPOINTS = {
  // Admin data
  ADMIN_DATA_FARMS: '/api/admin-data/farms',
  
  // Notifications
  NOTIFICATIONS: '/api/notifications',
  NOTIFICATIONS_STREAM: '/api/notifications/stream',
  NOTIFICATIONS_STATS: '/api/notifications/stats',
  NOTIFICATIONS_MARK_READ: '/api/notifications/:id/read',
  NOTIFICATIONS_MARK_ALL_READ: '/api/notifications/mark-all-read',
  NOTIFICATIONS_CLEAR_ALL: '/api/notifications/clear-all',
  NOTIFICATIONS_HEALTH: '/api/notifications/health',
  NOTIFICATION_SETTINGS: '/api/notification-settings',
  NOTIFICATION_SETTINGS_RESET: '/api/notification-settings/reset',
  NOTIFICATION_TYPES: '/api/notification-settings/types',
  SUPPORT_STATUS: '/api/support/status',
  
  // Support Tickets (BarnX Operations proxy)
  SUPPORT_TICKETS: '/api/support/tickets',
  
  // Feed management (legacy)
  FEED_LIST: '/api/feed-list',
  FEED_SETTINGS: '/api/feed-settings',
  FEED_SESSIONS: '/api/feed-sessions',
  FEED_RECORDS: '/api/feed-records',
  FEED_RECORDS_RESCHEDULE: '/api/feed-records/reschedule',
  INDIVIDUAL_FEED_RECORD: '/api/individual-feed-record',
  RECHECKS_SIMPLE: '/api/rechecks-simple',
  RECHECK: '/api/recheck',
  COMPLETED_COWS: '/api/completed-cows',
  
  // Enhanced Feed Management System
  FEED_ITEMS: '/api/feed-management/items',
  FEED_INVENTORY: '/api/feed-management/inventory',
  FEED_ANALYSIS: '/api/feed-management/analysis',
  FEED_USAGE: '/api/feed-management/usage',
  FEED_DASHBOARD: '/api/feed-management/dashboard',
  FEED_ANALYTICS: '/api/feed-management/analytics',
  
  // Farm Settings - Using new consolidated atomic endpoint
  FARM_SETTINGS: '/api/farm-settings',
  FARM_SETTINGS_PREDICTION_DEFAULTS: '/api/farm-settings/prediction-defaults',
  FARM_SETTINGS_FEED_CATEGORIES: '/api/feed-management/farm-settings/feed-categories',
  FARM_SETTINGS_RESET: '/api/feed-management/farm-settings/reset',
  
  // Currency endpoints
  CURRENCY_RATES: '/api/currency/rates',
  CURRENCY_RATES_FETCH: '/api/currency/rates/fetch-latest',
  FARM_CURRENCY: '/api/farms/:farmId/currency',
  
  // Cow management
  COW_REFERRALS: '/api/cow-referrals',
  COW_WITHDRAWALS: '/api/cow-withdrawals',
  COW_PRIORITY: '/api/cow-priority',
  COW_MANAGEMENT: '/api/cow-management',
  COW_ACTIVE_LESIONS: '/api/cow-active-lesions',
  COW_HISTORY: '/api/cow-history',
  
  // Files
  FILES: '/api/files',
  FILE_UPLOAD: '/api/files/upload',
  
  // Chat
  CHAT: '/api/chat',
  CHAT_CONSULTANTS: '/api/chat/consultants',
  
  // AI Assistant
  AI_ASSISTANT_CHAT: '/api/ai-assistant/chat',
  AI_ASSISTANT_CONVERSATIONS: '/api/ai-assistant/conversations',
  AI_ASSISTANT_CONVERSATION: '/api/ai-assistant/conversations/:id',
  
  // Teams & Accounts
  ACCOUNTS: '/api/accounts',
  
  // Corporate Account Management
  CORPORATE_ACCOUNT: '/api/corporate/:id',
  CORPORATE_INDIVIDUAL_ACCOUNTS: '/api/corporate/:id/individual-accounts',
  CORPORATE_STATS: '/api/corporate/:id/stats',
  CORPORATE_SUB_USERS: '/api/corporate/:id/sub-users',
  CORPORATE_ASSIGN_ACCESS: '/api/corporate/:id/assign-access',
  CORPORATE_REMOVE_ACCESS: '/api/corporate/:id/remove-access',
  
  // Team Management (Individual Account Sub-Users)
  TEAM_SUB_USERS: '/api/users/sub-users',
  TEAM_INVITE_SUB_USER: '/api/users/invite-sub-user',
  TEAM_SUB_USER_STATS: '/api/users/sub-user-stats',
  
  // Consultant
  CONSULTANT_FARMS: '/api/consultant/farms',
  
  // Billing
  BILLING_SUBSCRIPTION: '/api/billing/subscription',
  BILLING_HISTORY: '/api/billing/history',
  BILLING_PLANS: '/api/billing/plans',
  BILLING_CUSTOMER_PORTAL: '/api/billing/customer-portal',
  BILLING_INVOICE: '/api/billing/invoice',
  BILLING_CHANGE_PLAN: '/api/billing/change-plan',
  BILLING_PLAN_CHANGE_REQUEST: '/api/billing/plan-change-request',
  BILLING_CANCELLATION_REQUEST: '/api/billing/cancellation-request',
  // Pricing (public)
  PRICING_TIERS: '/api/pricing/tiers',
  PRICING_QUOTE: '/api/pricing/quote',
  
  // Admin
  ADMIN_NOTIFICATIONS: '/api/admin/notifications',
  ADMIN_USERS: '/api/admin/users',
  ADMIN_ACCOUNTS: '/api/admin/accounts',
  
  // Tutorial Management
  ADMIN_TUTORIALS: '/api/admin/tutorials',
  ADMIN_TUTORIAL: '/api/admin/tutorials/:id',
  
  // Rations management
  RATIONS_ANIMAL_GROUPS: '/api/rations/animal-groups',
  RATIONS_ANIMAL_GROUP: '/api/rations/animal-groups/:id',
  RATIONS_SUB_GROUPS: '/api/rations/animal-groups/:groupId/sub-groups',
  RATIONS_SUB_GROUP: '/api/rations/sub-groups/:id',
  RATIONS: '/api/rations',
  RATIONS_SINGLE: '/api/rations/:id',
  RATIONS_DUPLICATE: '/api/rations/:id/duplicate',
  RATIONS_ACTIVATE: '/api/rations/:id/activate',
  RATIONS_INGREDIENTS: '/api/rations/:id/ingredients',
  INGREDIENTS: '/api/rations/ingredients',
  INGREDIENT: '/api/rations/ingredients/:id',
  
  // Global Feed Library
  GLOBAL_FEEDS: '/api/global-feeds',
  GLOBAL_FEED: '/api/global-feeds/:id',
  GLOBAL_FEEDS_IMPORT: '/api/global-feeds/import-csv',
  GLOBAL_FEEDS_EXPORT: '/api/global-feeds/export/csv',
  
  // Corporate Feed Library
  CORPORATE_FEEDS: '/api/corporate-feeds',
  CORPORATE_FEED: '/api/corporate-feeds/:id',
  CORPORATE_FEEDS_IMPORT: '/api/corporate-feeds/import-csv',
  CORPORATE_FEEDS_EXPORT: '/api/corporate-feeds/export/csv',
  
  // Cow Group Profiles
  COW_PROFILES: '/api/cow-profiles',
  COW_PROFILE: '/api/cow-profiles/:id',
  COW_PROFILE_BY_GROUP: '/api/cow-profiles/by-animal-group/:animalGroupId',
  
  // Predictions
  PREDICTIONS: '/api/predictions',
  PREDICTION: '/api/predictions/:id',
  PREDICTIONS_RUN: '/api/predictions/run',
  PREDICTIONS_HISTORY: '/api/predictions/ration/:rationId/history',
  PREDICTIONS_COMPARE: '/api/predictions/compare',
  ADMIN_FEED_RECORDS: '/api/admin/feed-records',
  ADMIN_COW_MANAGEMENT: '/api/admin/cow-management',
  ADMIN_COW_ACTIVE_LESIONS: '/api/admin/cow-active-lesions',
  ADMIN_COW_PRIORITY: '/api/admin/cow-priority',
  ADMIN_ADD_COW: '/api/admin/add-cow',
  ADMIN_BULK_COW_OPERATIONS: '/api/admin/bulk-cow-operations',
  ADMIN_HERD_DATA_UPLOADS: '/api/admin/herd-data-uploads',
  ADMIN_HERD_DATA_UPLOAD: '/api/admin/herd-data-upload',
  ADMIN_FEED_RECORD_UPLOADS: '/api/admin/feed-record-uploads',
  ADMIN_FEED_RECORD_UPLOAD: '/api/admin/feed-record-upload',
  ADMIN_RECALCULATE_LESION_STATISTICS: '/api/admin/recalculate-lesion-statistics',
  RECALCULATE_COW_LESION_STATISTICS: '/api/recalculate-cow-lesion-statistics',
  
  
  // Auth endpoints
  AUTH_LOGIN: '/api/auth/login',
  AUTH_REGISTER: '/api/auth/register', 
  AUTH_CHECK_EMAIL: '/api/auth/check-email',
  AUTH_ME: '/api/auth/me',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_FORGOT_PASSWORD: '/api/auth/forgot-password',
  AUTH_RESET_PASSWORD: '/api/auth/reset-password',
  
  // Cloudinary upload endpoints
  ADMIN_UPLOAD_HELP_IMAGE: '/api/admin/upload-help-image',
  ADMIN_LIST_HELP_IMAGES: '/api/admin/list-help-images',
  ADMIN_UPLOAD_SITE_ASSET: '/api/admin/upload-site-asset',
  ADMIN_UPLOAD_LANDING_CLOUDINARY: '/api/admin/upload-landing-cloudinary',
  ADMIN_DELETE_CLOUDINARY_IMAGE: '/api/admin/cloudinary-image',
  ADMIN_SUBSCRIPTION_PLANS: '/api/admin/subscription-plans',
  ADMIN_SUBSCRIPTIONS: '/api/admin/subscriptions',
  ADMIN_STATS: '/api/admin/stats',
  ADMIN_STRIPE_STATUS: '/api/admin/stripe/status',
  ADMIN_STRIPE_WEBHOOKS: '/api/admin/stripe/webhooks',
  ADMIN_STRIPE_IMPORT_PRODUCTS: '/api/admin/stripe/import-products',
  ADMIN_STRIPE_SYNC: '/api/admin/stripe/sync',
  ADMIN_STRIPE_IMPORT_PRODUCT: '/api/admin/stripe/import-product',
  ADMIN_ANALYTICS_SUBSCRIPTIONS: '/api/admin/analytics/subscriptions',
  ADMIN_SYNC_STATUS: '/api/admin/sync/status',
  ADMIN_SYNC_QUICK_FIX: '/api/admin/sync/quick-fix',
  ADMIN_SYNC_FULL_SYNC: '/api/admin/sync/full-sync',
  
  // Other endpoints
  SUBSCRIPTION_PLANS: '/api/subscription-plans',
  CREATE_CHECKOUT_SESSION: '/api/create-checkout-session',
};

// Helper function to create full API URL
export const createApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

// Helper function to get authentication token
export const getAuthToken = (): string | null => {
  return localStorage.getItem('userToken') || localStorage.getItem('adminToken');
};

// Helper function to set authentication token (synchronously for immediate use)
const setAuthToken = (token: string): void => {
  // Determine which token storage to update based on current user type
  const isAdmin = !!localStorage.getItem('adminToken');
  if (isAdmin) {
    localStorage.setItem('adminToken', token);
  } else {
    localStorage.setItem('userToken', token);
  }
  
  // Also update the metadata for persistentStorage compatibility
  const key = isAdmin ? 'adminToken' : 'userToken';
  localStorage.setItem(`${key}_meta`, JSON.stringify({
    timestamp: Date.now(),
    expiresAt: Date.now() + (24 * 7 * 60 * 60 * 1000) // 7 days
  }));
};

// Helper function for authenticated requests
export const createAuthHeaders = (token?: string): HeadersInit => {
  // ✅ CONSOLIDATION: Use unified auth token approach
  const authToken = token || getAuthToken();
  const isAdminRequest = localStorage.getItem('adminToken') && !token;
  
  return {
    'Content-Type': 'application/json',
    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
    ...(isAdminRequest && { 'X-Admin-Request': 'true' }),
  };
};

// Token refresh mutex to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const isAccessTokenExpired = (token: string, skewMs = 5000): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp !== 'number' || Date.now() >= payload.exp * 1000 - skewMs;
  } catch {
    return true;
  }
};

// Centralized token refresh function
const refreshAuthToken = async (): Promise<string | null> => {
  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('🔄 Token expired, attempting refresh...');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const refreshResp = await fetch(createApiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
      });

      if (refreshResp.ok) {
        const data = await refreshResp.json();
        const newToken = data.token;
        
        if (newToken) {
          setAuthToken(newToken);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
            localStorage.setItem('refreshToken_meta', JSON.stringify({
              timestamp: Date.now(),
              expiresAt: Date.now() + (24 * 30 * 60 * 60 * 1000)
            }));
          }
          
          if (typeof window !== 'undefined') {
            const isAdmin = !!localStorage.getItem('adminToken');
            window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
              detail: { token: newToken, userType: isAdmin ? 'admin' : 'user' } 
            }));
          }
          
          console.log('✅ Token refreshed and stored successfully');
          return newToken;
        } else {
          console.error('❌ No token in refresh response');
          return null;
        }
      } else {
        console.error('❌ Token refresh failed:', refreshResp.status);
        
        if (refreshResp.status === 401) {
          const currentToken = getAuthToken();
          if (!currentToken || isAccessTokenExpired(currentToken)) {
            localStorage.removeItem('userToken');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('refreshToken');
          }
        }
        return null;
      }
    } catch (refreshError) {
      console.error('❌ Token refresh error:', refreshError);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const ensureValidAccessToken = async (): Promise<string | null> => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  if (!isAccessTokenExpired(token)) {
    return token;
  }
  return refreshAuthToken();
};

// Standard API request wrapper with automatic token refresh
export const apiRequest = async <T = any>(
  endpoint: string,
  method: string = 'GET',
  body?: any,
  options: RequestInit = {},
  farmId?: string // Optional farm ID for farm-scoped requests
): Promise<T> => {
  const url = createApiUrl(endpoint);

  let resolvedFarmId = farmId;
  if (!resolvedFarmId) {
    const queryMatch = endpoint.match(/[?&]farmId=([^&]+)/);
    if (queryMatch) {
      resolvedFarmId = decodeURIComponent(queryMatch[1]);
    }
  }
  if (!resolvedFarmId) {
    try {
      const savedFarm = await getSelectedFarm();
      resolvedFarmId =
        savedFarm?.farmId ||
        savedFarm?.farm?.id ||
        undefined;
    } catch {
      // Ignore storage read errors; request may still succeed via query param
    }
  }
  if (!resolvedFarmId && body?.farmId) {
    resolvedFarmId = body.farmId;
  }

  await ensureValidAccessToken();
  const headers = createAuthHeaders();
  
  const requestOptions: RequestInit = {
    method,
    headers: {
      ...headers,
      ...(resolvedFarmId && { 'x-farm-id': resolvedFarmId }),
      ...options.headers,
    },
    ...options,
    // Ensure cookies are sent for refresh flows when needed
    credentials: options.credentials || 'include',
  };
  
  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    requestOptions.body = JSON.stringify(body);
  }
  
  try {
    let response = await fetch(url, requestOptions);

    // If unauthorized, try to refresh token and retry once
    if (response.status === 401 && !endpoint.includes('/auth/')) {
      const newToken = await refreshAuthToken();
      
      if (newToken) {
        // Retry the original request with the new token
        console.log('🔄 Retrying original request with refreshed token');
        const retryHeaders = createAuthHeaders(newToken);
        const retryOptions: RequestInit = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            ...retryHeaders,
          },
        };
        
        response = await fetch(url, retryOptions);
      } else {
        // Refresh failed, throw error to trigger logout/redirect
        throw new Error('Session expired. Please log in again.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle 403 (farm access denied) with helpful message
      if (response.status === 403 && errorData.availableFarms) {
        const farmList = errorData.availableFarms
          .map((f: any) => `${f.farmName} (${f.accessType})`)
          .join(', ');
        throw new Error(
          `${errorData.error || 'Access denied'}. Available farms: ${farmList}`
        );
      }
      
      const detailParts = [
        errorData.error,
        errorData.constraint,
        errorData.suggestion,
        errorData.details,
      ].filter(Boolean);
      throw new Error(
        detailParts.length > 0
          ? detailParts.join(' — ')
          : `HTTP ${response.status}: ${response.statusText}`
      );
    }
    
    return response.json();
  } catch (error) {
    // Re-throw network errors with more context
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(`Network error: ${error.message}`);
    }
    throw error;
  }
};

// Authenticated fetch wrapper for non-JSON responses (e.g., file downloads, streams)
// This should be used by services that need direct fetch access with automatic token refresh
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = await ensureValidAccessToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
    credentials: options.credentials || 'include',
  };

  let response = await fetch(url, requestOptions);

  // If unauthorized, try to refresh and retry once
  if (response.status === 401 && !url.includes('/auth/')) {
    const newToken = await refreshAuthToken();

    if (newToken) {
      console.log('🔄 Retrying fetch with refreshed token');
      headers.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(url, { ...requestOptions, headers });
    }
  }

  return response;
};
